import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  Container,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  makeStyles
} from "@material-ui/core";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from "@material-ui/icons";
import PageHeader from "components/layout/page-header/PageHeader";
import Alert from "@material-ui/lab/Alert";
import { Screenshot } from "components/screenshot/Screenshot";

import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import api from "api";
import "./styles/PhotoGallery.css";

const useStyles = makeStyles(theme => ({
  iconButton: {
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  lightboxButton: {
    color: "white",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  }
}));

const PhotoGallery = ({ token, isPublic = false }) => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { project, date, folder_path: folderPathFromState } = location.state || {};
  const imageGalleryRef = useRef(null);
  const screenshotTagReference = useRef(null);

  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [projectName, setProjectName] = useState(project?.name || "");
  const [projectDate, setProjectDate] = useState(date || "");

  const lightboxSlides = useMemo(
    () => (photos.length > 0 ? photos.map(p => ({ src: p.original })) : []),
    [photos]
  );

  const shareToken = useMemo(() => {
    if (!isPublic) return null;
    const search = new URLSearchParams(location.search);
    return search.get("token");
  }, [isPublic, location.search]);

  useEffect(() => {
    if (!isPublic) {
      setProjectName(project?.name || "");
      setProjectDate(date || "");
    }
  }, [isPublic, project, date]);

  const inferProjectInfo = file => {
    if (!file) return {};
    const source = file.path || file.key || file.url;
    if (!source) return {};

    try {
      const base =
        typeof window !== "undefined" && window.location
          ? window.location.origin
          : undefined;
      const parsedUrl = base ? new URL(source, base) : new URL(source);
      const segments = parsedUrl.pathname.split("/").filter(Boolean);
      const projetosIndex = segments.findIndex(
        segment => segment === "projetos"
      );

      if (projetosIndex !== -1 && segments.length > projetosIndex + 3) {
        const typeSegment = segments[projetosIndex + 1];
        if (typeSegment === "fotos") {
          return {
            name: decodeURIComponent(segments[projetosIndex + 2] || ""),
            date: decodeURIComponent(segments[projetosIndex + 3] || "")
          };
        }
      }
    } catch (error) {
      console.warn(
        "Não foi possível inferir informações do projeto a partir da URL compartilhada.",
        error
      );
    }

    return {};
  };

  useEffect(() => {
    if (isPublic && !shareToken) {
      setError("Token público não fornecido.");
      setLoading(false);
      return;
    }

    if (!isPublic && (!project?.name || !date || !token)) {
      setError("Projeto, data ou token não fornecidos.");
      setLoading(false);
      return;
    }
    const abortController = new AbortController();
    const fetchPhotos = async () => {
      setLoading(true);
      try {
        let response;

        if (isPublic) {
          response = await api.get(
            `${process.env.REACT_APP_API_URL}/projects/shared/${shareToken}`,
            { signal: abortController.signal }
          );
        } else {
          response = await api.post(
            `/projects/list_files`,
            { folder_path: folderPathFromState || `projetos/${project.name}/${date}/fotos` },
            { headers: { Authorization: `Bearer ${token}` }, signal: abortController.signal }
          );
        }
        const photoFiles = response.data.files;
        if (photoFiles && photoFiles.length > 0) {
          setPhotos(
            photoFiles.map(file => ({
              original: file.url,
              thumbnail: file.url
            }))
          );
          if (isPublic) {
            const info = inferProjectInfo(photoFiles[0]);
            if (info.name) {
              setProjectName(info.name);
            }
            if (info.date) {
              setProjectDate(info.date);
            }
          }
        } else {
          setError("Nenhuma foto encontrada.");
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Erro ao buscar fotos:", err);
          setError("Erro ao carregar as fotos.");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchPhotos();
    return () => abortController.abort();
  }, [project, date, token, isPublic, shareToken]);

  useEffect(() => {
    if (isPublic) return;

    const updateTagReference = () => {
      const lightboxSlide = document.querySelector(".yarl__slide_current");
      if (lightboxSlide) {
        screenshotTagReference.current = lightboxSlide;
        return;
      }

      const gallerySlide = document.querySelector(
        ".image-gallery-slide.image-gallery-center"
      );
      if (gallerySlide) {
        screenshotTagReference.current = gallerySlide;
        return;
      }

      screenshotTagReference.current = null;
    };

    // Espera o DOM atualizar (principalmente ao abrir/alternar o lightbox)
    const t = window.setTimeout(updateTagReference, 0);
    updateTagReference();

    return () => window.clearTimeout(t);
  }, [isPublic, lightboxOpen, currentIndex, photos]);

  const openLightbox = index => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const toolbarButtons = [
    <IconButton
      key="close-button"
      onClick={() => setLightboxOpen(false)}
      className={classes.lightboxButton}
    >
      <CloseIcon />
    </IconButton>
  ];

  const reportsFolderPath =
    folderPathFromState ||
    (project?.name && date
      ? `projetos/${project.name}/${date}/fotos`
      : null);

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Container maxWidth="md" style={{ paddingTop: "2rem" }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );

  const galleryTitle =
    (projectName || "Galeria") + (projectDate ? ` — ${projectDate}` : "");

  const backButton = (
    <Tooltip title="Voltar">
      <IconButton
        aria-label="Voltar"
        onClick={() => navigate("/projects", { state: { openProject: project?.name } })}
        size="small"
        className={classes.iconButton}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <div className="galleryRoot">
      <PageHeader
        accent="Galeria de fotos"
        title={galleryTitle}
        subtitle={projectName ? "Visualização das fotos do projeto" : undefined}
        leftAdornment={backButton}
      />

      <Container maxWidth="xl" style={{ marginTop: "1rem" }}>
        {photos.length > 0 && (
          <>
            <div className="gallery-scroll-fix-container">
              <ImageGallery
                ref={imageGalleryRef}
                items={photos}
                thumbnailPosition="left"
                showPlayButton={false}
                showFullscreenButton={false}
                onClick={() =>
                  openLightbox(imageGalleryRef.current.getCurrentIndex())
                }
                lazyLoad={true}
              />
            </div>
          </>
        )}
      </Container>

      {!isPublic && (
        <Screenshot
          tagReference={screenshotTagReference}
          folderPath={
            reportsFolderPath ||
            `projetos/${project?.name || ""}/${date || ""}/fotos`
          }
          variant="inline"
          hintPlacement="rightOfToolbar"
        />
      )}

      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          plugins={[Zoom]}
          zoom={{
            maxZoomPixelRatio: 5,
            scrollToZoom: true
          }}
          index={currentIndex}
          slides={lightboxSlides}
          toolbar={{
            buttons: toolbarButtons
          }}
        />
      )}

    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(PhotoGallery);
